import React from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";

// Fixed categorical domains used by charts
const REASONS = [
  "Academic",
  "Behavioral",
  "Social/Emotional",
  "Family-related",
  "Attendance",
  "Unknown",
];
const SUPPORTS = [
  "IEP",
  "504",
  "RTI Tier 1",
  "RTI Tier 2",
  "RTI Tier 3",
  "Counseling only",
  "Unknown",
];

const Analysis = () => {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = React.useState(null);
  const [students, setStudents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [openReferral, setOpenReferral] = React.useState(false);
  const [openGrade, setOpenGrade] = React.useState(false);
  const [openReasonSupport, setOpenReasonSupport] = React.useState(false);
  const [openSupportStatus, setOpenSupportStatus] = React.useState(false);

  React.useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("token");
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const res = await fetch(`${apiUrl.replace(/\/$/, "")}/me`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        if (!res.ok) throw new Error("Failed to fetch user profile");
        const data = await res.json();
        setUserProfile(data);
      } catch {
        setUserProfile(null);
      }
    };
    fetchProfile();
    const fetchStudents = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const res = await fetch(`${apiUrl.replace(/\/$/, "")}/students`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        if (!res.ok) throw new Error("Failed to fetch students");
        const data = await res.json();
        setStudents(Array.isArray(data.students) ? data.students : []);
      } catch (e) {
        setError(e.message || "Failed to load students");
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, []);

  // Group by referredBy/type
  const counts = React.useMemo(() => {
    const map = new Map();
    for (const s of students) {
      const keyRaw = s.referredBy ?? s.referralType ?? "Unknown";
      const key = String(keyRaw || "Unknown").trim() || "Unknown";
      map.set(key, (map.get(key) || 0) + 1);
    }
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [students]);

  const COLORS = [
    "#2563eb",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#14b8a6",
    "#f97316",
    "#22c55e",
    "#0ea5e9",
  ];

  // Students by grade distribution
  const gradeCounts = React.useMemo(() => {
    const map = new Map();
    for (const s of students) {
      let g = s.grade;
      if (g === undefined || g === null || g === "") g = "Unknown";
      const key = String(g).trim() || "Unknown";
      map.set(key, (map.get(key) || 0) + 1);
    }
    const arr = Array.from(map.entries()).map(([name, value]) => ({
      name,
      value,
    }));
    // Sort: numeric grades ascending first, then non-numeric, Unknown last
    arr.sort((a, b) => {
      if (a.name === "Unknown") return 1;
      if (b.name === "Unknown") return -1;
      const an = parseInt(a.name, 10);
      const bn = parseInt(b.name, 10);
      const aNum = !Number.isNaN(an);
      const bNum = !Number.isNaN(bn);
      if (aNum && bNum) return an - bn;
      if (aNum) return -1;
      if (bNum) return 1;
      return String(a.name).localeCompare(String(b.name));
    });
    return arr;
  }, [students]);

  // Reason for Referral by Student Support Status (stacked bar)
  const reasonSupportData = React.useMemo(() => {
    // Initialize data map with zeroes for each reason
    const map = new Map();
    for (const r of REASONS) {
      const base = { reason: r };
      for (const s of SUPPORTS) base[s] = 0;
      map.set(r, base);
    }
    const normalize = (val, allowed) => {
      const v = String(val ?? "Unknown").trim();
      return allowed.includes(v) ? v : "Unknown";
    };
    for (const s of students) {
      const rr = normalize(s.referredReason, REASONS);
      const raw = s.studentSupportStatus;
      const statuses = Array.isArray(raw)
        ? raw.map((v) => normalize(v, SUPPORTS))
        : [normalize(raw, SUPPORTS)];
      const row = map.get(rr) || { reason: rr };
      for (const sup of statuses) {
        if (row[sup] == null) row[sup] = 0;
        row[sup] += 1;
      }
      map.set(rr, row);
    }
    return Array.from(map.values());
  }, [students]);

  // Student Support Status distribution (donut)
  const supportStatusCounts = React.useMemo(() => {
    const map = new Map();
    for (const s of students) {
      const raw = s.studentSupportStatus;
      const arr = Array.isArray(raw) ? raw : [raw];
      for (const v of arr) {
        const vv = String(v ?? "Unknown").trim() || "Unknown";
        const key = SUPPORTS.includes(vv) ? vv : "Unknown";
        map.set(key, (map.get(key) || 0) + 1);
      }
    }
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [students]);

  return (
    <div style={{ background: "#f3f4f6", minHeight: "100vh" }}>
      <nav
        style={{
          background: "linear-gradient(90deg, #23395d 60%, #2d4a7a 100%)",
          color: "#fff",
          padding: "0 0 0 32px",
          display: "flex",
          alignItems: "center",
          height: 64,
          fontWeight: 500,
          fontSize: 18,
          letterSpacing: 0.5,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 22, marginRight: 40 }}>
          COUNSELOR NOTES
        </span>
        <button
          style={{
            color: "#fff",
            background: "none",
            border: "none",
            textDecoration: "none",
            marginRight: 32,
            font: "inherit",
            cursor: "pointer",
          }}
          onClick={() => navigate("/dashboard")}
        >
          Home
        </button>
        <button
          style={{
            color: "#fff",
            background: "none",
            border: "none",
            textDecoration: "none",
            marginRight: 32,
            font: "inherit",
            cursor: "pointer",
          }}
          onClick={() => navigate("/support-plans")}
        >
          Support Plans
        </button>
        <button
          style={{
            color: "#fff",
            background: "none",
            border: "none",
            textDecoration: "none",
            marginRight: 32,
            font: "inherit",
            cursor: "pointer",
          }}
          onClick={() => navigate("/analysis")}
        >
          Analysis
        </button>
        {(userProfile?.role === "admin" ||
          userProfile?.role === "districtAdmin" ||
          userProfile?.role === "auditor") && (
          <button
            style={{
              color: "#fff",
              background: "none",
              border: "1px solid rgba(255,255,255,0.6)",
              textDecoration: "none",
              marginRight: 12,
              font: "inherit",
              cursor: "pointer",
              borderRadius: 6,
              padding: "6px 10px",
            }}
            onClick={() => navigate("/admin-tools")}
          >
            Admin Tools
          </button>
        )}
        <button
          onClick={() => {
            localStorage.removeItem("token");
            navigate("/login");
          }}
          style={{
            background: "#fff",
            color: "#23395d",
            border: "none",
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 16,
            padding: "8px 22px",
            cursor: "pointer",
            marginLeft: "auto",
            marginRight: 24,
          }}
        >
          Log Out
        </button>
      </nav>
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 600, margin: "32px 0 8px 0" }}>
          Analysis
        </h1>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          <li
            role="button"
            tabIndex={0}
            aria-expanded={openReferral}
            aria-controls="referral-chart"
            onClick={() => setOpenReferral((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpenReferral((v) => !v);
              }
            }}
            style={{
              textAlign: "center",
              fontWeight: 700,
              fontSize: 18,
              color: "#0f172a",
              padding: "10px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              background: "#fff",
              cursor: "pointer",
              userSelect: "none",
              marginBottom: 10,
            }}
          >
            Referral Types across all students in your school{" "}
            {openReferral ? "▴" : "▾"}
          </li>
          {openReferral && (
            <li id="referral-chart" style={{ marginBottom: 20 }}>
              {loading ? (
                <div>Loading...</div>
              ) : error ? (
                <div style={{ color: "#ef4444" }}>{error}</div>
              ) : counts.length === 0 ? (
                <div style={{ color: "#64748b" }}>No data available.</div>
              ) : (
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    padding: 16,
                    height: 420,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={counts}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={140}
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {counts.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </li>
          )}
        </ul>

        {/* Reason for Referral by Support Status (Stacked Bar) */}
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          <li
            role="button"
            tabIndex={0}
            aria-expanded={openReasonSupport}
            aria-controls="reason-support-chart"
            onClick={() => setOpenReasonSupport((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpenReasonSupport((v) => !v);
              }
            }}
            style={{
              textAlign: "center",
              fontWeight: 700,
              fontSize: 18,
              color: "#0f172a",
              padding: "10px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              background: "#fff",
              cursor: "pointer",
              userSelect: "none",
              margin: "14px 0 10px",
            }}
          >
            Reason for Referral by Support Status{" "}
            {openReasonSupport ? "▴" : "▾"}
          </li>
          {openReasonSupport && (
            <li id="reason-support-chart" style={{ marginBottom: 8 }}>
              {loading ? (
                <div>Loading...</div>
              ) : error ? (
                <div style={{ color: "#ef4444" }}>{error}</div>
              ) : reasonSupportData.length === 0 ? (
                <div style={{ color: "#64748b" }}>No data available.</div>
              ) : (
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    padding: 16,
                    height: 460,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={reasonSupportData}
                      margin={{ top: 16, right: 24, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="reason"
                        label={{
                          value: "Reason",
                          position: "insideBottom",
                          offset: -4,
                        }}
                      />
                      <YAxis
                        allowDecimals={false}
                        label={{
                          value: "Students",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />
                      <Tooltip />
                      <Legend />
                      {SUPPORTS.map((k, idx) => (
                        <Bar
                          key={k}
                          dataKey={k}
                          stackId="a"
                          fill={COLORS[idx % COLORS.length]}
                          radius={[4, 4, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </li>
          )}
        </ul>

        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          <li
            role="button"
            tabIndex={0}
            aria-expanded={openGrade}
            aria-controls="grade-chart"
            onClick={() => setOpenGrade((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpenGrade((v) => !v);
              }
            }}
            style={{
              textAlign: "center",
              fontWeight: 700,
              fontSize: 18,
              color: "#0f172a",
              padding: "10px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              background: "#fff",
              cursor: "pointer",
              userSelect: "none",
              margin: "14px 0 10px",
            }}
          >
            Students by Grade {openGrade ? "▴" : "▾"}
          </li>
          {openGrade && (
            <li id="grade-chart" style={{ marginBottom: 8 }}>
              {loading ? (
                <div>Loading...</div>
              ) : error ? (
                <div style={{ color: "#ef4444" }}>{error}</div>
              ) : gradeCounts.length === 0 ? (
                <div style={{ color: "#64748b" }}>No data available.</div>
              ) : (
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    padding: 16,
                    height: 420,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={gradeCounts}
                      margin={{ top: 16, right: 24, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        label={{
                          value: "Grade",
                          position: "insideBottom",
                          offset: -4,
                        }}
                      />
                      <YAxis
                        allowDecimals={false}
                        label={{
                          value: "Students",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="value"
                        name="Students"
                        fill="#2563eb"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </li>
          )}
        </ul>

        {/* Student Support Status (Donut) */}
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          <li
            role="button"
            tabIndex={0}
            aria-expanded={openSupportStatus}
            aria-controls="support-status-chart"
            onClick={() => setOpenSupportStatus((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpenSupportStatus((v) => !v);
              }
            }}
            style={{
              textAlign: "center",
              fontWeight: 700,
              fontSize: 18,
              color: "#0f172a",
              padding: "10px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              background: "#fff",
              cursor: "pointer",
              userSelect: "none",
              margin: "14px 0 10px",
            }}
          >
            Student Support Status {openSupportStatus ? "▴" : "▾"}
          </li>
          {openSupportStatus && (
            <li id="support-status-chart" style={{ marginBottom: 20 }}>
              {loading ? (
                <div>Loading...</div>
              ) : error ? (
                <div style={{ color: "#ef4444" }}>{error}</div>
              ) : supportStatusCounts.length === 0 ? (
                <div style={{ color: "#64748b" }}>No data available.</div>
              ) : (
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    padding: 16,
                    height: 420,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={supportStatusCounts}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={140}
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {supportStatusCounts.map((entry, index) => (
                          <Cell
                            key={`cell-ss-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default Analysis;
